import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import {
  categoryId,
  materialId,
  purchaseUnitOf,
  type SupplierCode,
  smallPerBigOf,
  supplierId,
  warehouseRmId
} from './wms-master.seed'
import {
  QC_ACTION_PATH,
  QC_ACTION_STATUS,
  type QcActionName,
  type QcStatusCode,
  qcActionId,
  qcActorId,
  qcStatusId,
  WF_QC_01_ID
} from './workflow-qc.seed'
import {
  type TobActorCode,
  type TobStatusCode,
  tobActionId,
  tobActorId,
  tobStatusId,
  WF_TOB_01_ID
} from './workflow-tob.seed'

/**
 * Inbound Receiving (C03.P04) — ERD v010: rm_arrival(+_line), pallet, rm_pallet_content,
 * rm_quarantine_entry, rm_qc_assignment, rm_tobacco_receipt(+_line), rm_unit, rm_putaway_task(+_item),
 * warehouse_zone, mt_rack, mt_rack_bin.
 * Sumber angka: seed prototype `jal-v7.44` (in.kedatangan, rm.inbound_batches, rm.pallets, rm.quarantine,
 * rm.qc_schedule, rm.tbk_terima, rm.putaway).
 * [A] Batch pada pallet karantina prototype tidak punya kepala kedatangan; kepala `IN-…` dibuat di sini
 * agar FK `arrival_line_id` (ERD) terpenuhi. Nomor Surat Jalannya rekaan (SJ-SUP-0001xx).
 */

const utc = (at: string) => new Date(at.replace(' ', 'T') + ':00+07:00').toISOString()

const audit = (userId: string, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: null,
  date_updated: null
})

/* ------------------------------------------------------------ lokasi rak */

const ZONE_ID = seedId(SEED_GROUP.warehouseZone, 1)
/** Zona Karantina NG (prototype `QN-NG`) — tujuan unit NG hasil QC. */
const NG_ZONE_ID = seedId(SEED_GROUP.warehouseZone, 2)
/** Area QC RM (prototype `ZQ-RM`) — gerbang tahap 1 putaway: barang hasil QC diambil dari sini. */
const QC_AREA_ZONE_ID = seedId(SEED_GROUP.warehouseZone, 3)

/** Alasan Not Good QC RM — `mt_reject_type` kategori `RM_QC` (prototype IRM-05 v7.34 `ALASAN_NG`). */
const RM_QC_REJECT_TYPES = [
  ['CUT', 'Cut to Cut'],
  ['WARNA', 'Warna tidak sesuai'],
  ['UKURAN', 'Ukuran tidak sesuai'],
  ['LEMBAP', 'Basah / lembap'],
  ['SOBEK', 'Kemasan sobek'],
  ['KARAT', 'Berkarat'],
  ['BENGKOK', 'Bengkok'],
  ['JAMUR', 'Kotor / berjamur'],
  ['LAIN', 'Lainnya']
] as const
const RACKS = [
  ['R01', 'Rack Tembakau', 'tembakau'],
  ['R02', 'Rack Umum', null],
  ['R03', 'Rack Umum 2', null]
] as const

/** Kode slot `R01-B02-L03-P01` (rack-bay-level-posisi), sama dengan Master Lokasi prototype. */
const BIN_CODES = [
  ...['B01', 'B02', 'B03', 'B04'].flatMap(bay => ['L01', 'L02', 'L03'].map(level => `R01-${bay}-${level}-P01`)),
  ...['B01', 'B02'].flatMap(bay => ['L01', 'L02'].map(level => `R02-${bay}-${level}-P01`)),
  ...['B01', 'B02'].flatMap(bay => ['L01', 'L02'].map(level => `R03-${bay}-${level}-P01`))
]

/**
 * Stok awal (saldo 1 Agu, `wms-master.seed.ts`) berdiri di rak umum / tembakau sesuai kategori — supaya daftar
 * Koreksi Penempatan (IRM-08) menampilkan lokasi tercatat. Kategori rak dijaga BR-04.
 */
export const OPENING_STOCK_RACK: Record<string, string> = {
  'TSG-MLD:20260801:01:0001': 'R01-B02-L01-P01',
  'ETK-BOLD20:20260801:01:0002': 'R03-B01-L01-P01',
  'FLT-B06:20260801:01:0003': 'R03-B01-L02-P01',
  'PPR-REG:20260801:01:0004': 'R03-B02-L01-P01',
  'ETK-XTRA20:20260801:01:0005': 'R03-B02-L02-P01'
}

export const rackBinId = (code: string) => seedId(SEED_GROUP.rackBin, BIN_CODES.indexOf(code) + 1)

/* --------------------------------------------------------- kedatangan */

type LineSeed = [material: string, po: string, qty: number, batch: string, palletized: boolean]
type ArrivalSeed = { no: string; sj: string; supplier: SupplierCode; at: string; lines: LineSeed[] }

/**
 * Urutan = id seed (indeks); kedatangan baru v7.50 ditambahkan di akhir supaya id yang sudah dipakai tidak bergeser.
 * D97 (2026-10-21): dua kedatangan PO tembakau prototipe (IN-260810-001 TSG-REG, IN-260817-001 TSG-MLD) dibuang —
 * Tembakau hanya masuk lewat Penerimaan Tembakau (`TOBACCO` di bawah), tidak pernah lewat PR/PO/karantina.
 */
const ARRIVALS: ArrivalSeed[] = [
  {
    no: 'IN-260801-001',
    sj: 'SJ-SUP-000155',
    supplier: 'SUP-BMJ',
    at: '2026-08-01 06:40',
    lines: [['ETK-BOLD20', 'PO-2607-088', 10, 'ETK-BOLD20-260801-01', true]]
  },
  {
    no: 'IN-260812-001',
    sj: 'SJ-SUP-000171',
    supplier: 'SUP-IJ',
    at: '2026-08-12 07:10',
    lines: [['ETK-XTRA20', 'PO-2608-091', 10, 'ETK-XTRA20-260812-01', true]]
  },
  {
    no: 'IN-260812-002',
    sj: 'SJ-SUP-000172',
    supplier: 'SUP-IJ',
    at: '2026-08-12 09:30',
    lines: [['KRF-75', 'PO-2608-103', 50, 'KRF-75-260812-01', true]]
  },
  {
    no: 'IN-260814-001',
    sj: 'SJ-SUP-000176',
    supplier: 'SUP-KTM',
    at: '2026-08-14 08:15',
    lines: [['LEM-C', 'PO-2608-102', 3, 'LEM-C-260814-01', true]]
  },
  {
    no: 'IN-260815-001',
    sj: 'SJ-SUP-000179',
    supplier: 'SUP-FLI',
    at: '2026-08-15 10:05',
    lines: [['FLT-SF13', 'PO-2608-104', 6, 'FLT-SF13-260815-01', true]]
  },
  {
    no: 'IN-260816-001',
    sj: 'SJ-SUP-000181',
    supplier: 'SUP-KH',
    at: '2026-08-16 07:45',
    lines: [['ETK-XTRA20', 'PO-2608-101', 4, 'ETK-XTRA20-260816-01', true]]
  },
  {
    no: 'IN-260817-002',
    sj: 'SJ-SUP-000184',
    supplier: 'SUP-BMJ',
    at: '2026-08-17 09:10',
    lines: [['OPP-125', 'PO-2608-110', 6, 'OPP-125-260817-01', true]]
  },
  {
    no: 'IN-260818-001',
    sj: 'SJ-SUP-000188',
    supplier: 'SUP-BMJ',
    at: '2026-08-18 08:10',
    lines: [
      ['PPR-REG', 'PO-2608-121', 4, 'PPR-REG-260818-01', false],
      ['ETK-ONE12', 'PO-2608-120', 3, 'ETK-ONE12-260818-01', false]
    ]
  },
  /*
   * v7.50: batch yang baru SEBAGIAN dipalletkan tetap "Menunggu Palletizing" (Partial Palletized dihapus, Q-09);
   * batch kedua sudah penuh di pallet terbuka PLT-000219 (belum ditutup ke karantina) → "Palletized".
   */
  {
    no: 'IN-260818-002',
    sj: 'SJ-SUP-000190',
    supplier: 'SUP-KH',
    at: '2026-08-18 11:05',
    lines: [
      ['ETK-XTRA20', 'PO-2608-122', 6, 'ETK-XTRA20-260818-01', false],
      ['INN-GLD79', 'PO-2608-122', 3, 'INN-GLD79-260818-01', true]
    ]
  },
  /* v7.50: hasil QC pallet PLT-000217 menunggu persetujuan Admin WH RM (demo "Setujui & Cetak Label"). */
  {
    no: 'IN-260817-003',
    sj: 'SJ-SUP-000186',
    supplier: 'SUP-BMJ',
    at: '2026-08-17 13:20',
    lines: [
      ['FOIL-BLK86', 'PO-2608-111', 6, 'FOIL-BLK86-260817-01', true],
      ['ETK-BOLD20', 'PO-2608-111', 4, 'ETK-BOLD20-260817-01', true]
    ]
  },
  /* v7.50: hasil QC pallet PLT-000218 dikembalikan admin ke Karno (chip "Dikembalikan admin"). */
  {
    no: 'IN-260816-002',
    sj: 'SJ-SUP-000182',
    supplier: 'SUP-KH',
    at: '2026-08-16 13:10',
    lines: [['INN-BLK96', 'PO-2608-105', 5, 'INN-BLK96-260816-01', true]]
  }
]

const lineIdByBatch = new Map<string, string>()
ARRIVALS.forEach((arrival, arrivalIndex) =>
  arrival.lines.forEach((line, lineIndex) =>
    lineIdByBatch.set(line[3], seedId(SEED_GROUP.arrivalLine, (arrivalIndex + 1) * 10 + lineIndex))
  )
)

/* ------------------------------------------------------ pallet & karantina */

/** [kode, siklus, isi (batch, qty), status]. Pallet kosong = sudah kembali dari putaway (daur ulang). */
const PALLETS: [code: string, cycle: number, contents: [batch: string, qty: number][], inQc: boolean][] = [
  [
    'PLT-000212',
    2,
    [
      ['ETK-XTRA20-260816-01', 4],
      ['LEM-C-260814-01', 3],
      ['FLT-SF13-260815-01', 6]
    ],
    true
  ],
  ['PLT-000213', 1, [['KRF-75-260812-01', 50]], true],
  /* D97: isi tembakau dibuang; kedua pallet kembali kosong (tersedia) agar kode pallet lain tidak bergeser. */
  ['PLT-000214', 3, [], false],
  ['PLT-000215', 0, [], false],
  ['PLT-000216', 4, [], false],
  [
    'PLT-000217',
    1,
    [
      ['FOIL-BLK86-260817-01', 6],
      ['ETK-BOLD20-260817-01', 4]
    ],
    true
  ],
  ['PLT-000218', 0, [['INN-BLK96-260816-01', 5]], true],
  [
    'PLT-000219',
    0,
    [
      ['ETK-XTRA20-260818-01', 2],
      ['INN-GLD79-260818-01', 3]
    ],
    false
  ],
  ['PLT-000220', 2, [], false],
  ['PLT-000221', 0, [], false]
]

const palletId = (code: string) => seedId(SEED_GROUP.pallet, PALLETS.findIndex(([c]) => c === code) + 1)

/** [no QRT, batch, pallet, masuk, qty good, qty ng, selesai QC?]. */
const QUARANTINE: [string, string, string | null, string, number, number, boolean][] = [
  ['QRT-260816-001', 'ETK-XTRA20-260816-01', 'PLT-000212', '2026-08-16 08:30', 0, 0, false],
  ['QRT-260814-002', 'LEM-C-260814-01', 'PLT-000212', '2026-08-14 09:00', 0, 0, false],
  ['QRT-260815-007', 'FLT-SF13-260815-01', 'PLT-000212', '2026-08-15 10:40', 0, 0, false],
  ['QRT-260812-003', 'KRF-75-260812-01', 'PLT-000213', '2026-08-12 10:00', 0, 0, false],
  ['QRT-260817-005', 'OPP-125-260817-01', null, '2026-08-17 09:40', 5, 1, true],
  ['QRT-260812-091', 'ETK-XTRA20-260812-01', null, '2026-08-12 07:40', 8, 2, true],
  ['QRT-260801-090', 'ETK-BOLD20-260801-01', 'PLT-000216', '2026-08-01 07:10', 10, 0, true],
  ['QRT-260817-008', 'FOIL-BLK86-260817-01', 'PLT-000217', '2026-08-17 14:00', 0, 0, false],
  ['QRT-260817-009', 'ETK-BOLD20-260817-01', 'PLT-000217', '2026-08-17 14:00', 0, 0, false],
  ['QRT-260816-010', 'INN-BLK96-260816-01', 'PLT-000218', '2026-08-16 14:00', 0, 0, false]
]

/** [no QSC, QRT, operator, qty, tanggal, jam, durasi, status WF-QC-01]. */
const QC_ASSIGNMENTS: [string, string, string, number, string, string, number, QcStatusCode][] = [
  ['QSC-260818-001', 'QRT-260817-005', 'karno', 6, '2026-08-18', '08:00', 60, 'QC_DONE'],
  ['QSC-260818-003', 'QRT-260816-001', 'tono', 4, '2026-08-18', '10:30', 60, 'QC_ASSIGNED'],
  ['QSC-260818-004', 'QRT-260814-002', 'tono', 3, '2026-08-18', '13:00', 60, 'QC_ASSIGNED'],
  ['QSC-260818-006', 'QRT-260815-007', 'tono', 6, '2026-08-18', '13:00', 60, 'QC_ASSIGNED'],
  ['QSC-260818-005', 'QRT-260812-003', 'karno', 30, '2026-08-18', '14:00', 90, 'QC_ASSIGNED'],
  ['QSC-260812-090', 'QRT-260812-091', 'karno', 10, '2026-08-12', '09:00', 60, 'QC_IN_STORAGE'],
  ['QSC-260801-085', 'QRT-260801-090', 'tono', 10, '2026-08-01', '08:00', 60, 'QC_IN_STORAGE'],
  ['QSC-260818-007', 'QRT-260817-008', 'karno', 6, '2026-08-18', '10:00', 60, 'QC_PENDING_APPROVAL'],
  ['QSC-260818-008', 'QRT-260817-009', 'karno', 4, '2026-08-18', '10:00', 60, 'QC_PENDING_APPROVAL'],
  ['QSC-260818-010', 'QRT-260816-010', 'karno', 5, '2026-08-18', '11:00', 60, 'QC_IN_PROGRESS']
]

/** Hasil QC assignment yang sudah selesai: [good, ng, alasan] (prototype `rm.quarantine` qty_good/qty_ng). */
const QC_RESULT_OF: Record<string, [number, number, string | null]> = {
  'QSC-260818-001': [5, 1, 'Cut to Cut'],
  'QSC-260812-090': [8, 2, 'Warna tidak sesuai'],
  'QSC-260801-085': [10, 0, null],
  /* v7.50 draf (Menunggu Persetujuan) dan hasil lama yang dikembalikan admin (nilai awal saat disimpan ulang). */
  'QSC-260818-007': [5, 1, 'Kemasan sobek'],
  'QSC-260818-008': [4, 0, null],
  'QSC-260818-010': [3, 2, 'Basah / lembap']
}

/**
 * Kode Good / NG yang dipesan saat draf disimpan (R-09, ERD-ADD `rm_qc_assignment.good_unit_code` / `ng_unit_code`).
 * Assignment lama yang sudah terbit ikut diisi supaya kolomnya konsisten dengan unit yang lahir darinya.
 */
const QC_RESERVED_CODES: Record<string, [string | null, string | null]> = {
  'QSC-260818-001': ['OPP-125:20260817:01:0001', 'OPP-125:20260817:01:0002'],
  'QSC-260818-007': ['FOIL-BLK86:20260817:01:0001', 'FOIL-BLK86:20260817:01:0002'],
  'QSC-260818-008': ['ETK-BOLD20:20260817:01:0001', null],
  'QSC-260818-010': ['INN-BLK96:20260816:01:0001', 'INN-BLK96:20260816:01:0002']
}

/** Riwayat aksi yang menyimpang dari jalur standar status — draf yang dikembalikan admin (R-08). */
const QC_PATH_OVERRIDE: Record<string, QcActionName[]> = {
  'QSC-260818-010': ['ACT_QC_SCHEDULE', 'ACT_QC_START', 'ACT_QC_RECORD', 'ACT_QC_RETURN']
}

/** Alasan pengembalian (log `ACT_QC_RETURN`, DERIVE — tidak ada kolom). */
const QC_RETURN_REASON: Record<string, string> = {
  'QSC-260818-010': 'Qty Not Good tidak sesuai hasil cek ulang — hitung lagi kemasan yang basah'
}

/** `qc_status` (ERD varchar) mengikuti kode status WF-QC-01 tanpa awalan `QC_` kecuali QC_DONE. */
export const QC_STATUS_COLUMN: Record<QcStatusCode, string> = {
  QC_ASSIGNED: 'ASSIGNED',
  QC_IN_PROGRESS: 'IN_PROGRESS',
  QC_PENDING_APPROVAL: 'PENDING_APPROVAL',
  QC_DONE: 'QC_DONE',
  QC_IN_STORAGE: 'IN_STORAGE'
}

/**
 * Hasil QC QSC-260818-001 (prototype `rm.units` UNIT-000012 + `rm.putaway` PWR-260818-001): Good menunggu
 * ditaruh di rak rekomendasi, NG menuju Karantina NG — satu tugas penempatan untuk operator yang sama.
 */
const QC_RESULT_UNITS: [uid: string, qr: string, qty: number, flag: 'REGULAR' | 'NG', reco: string | null][] = [
  ['UNIT-000012', 'OPP-125:20260817:01:0001', 5, 'REGULAR', 'R01-B03-L01-P01'],
  ['UNIT-000013', 'OPP-125:20260817:01:0002', 1, 'NG', null]
]

/* ------------------------------------------------------ penerimaan tembakau */

type TobStep = [action: string, actor: TobActorCode, by: string, at: string, reason?: string]

type TobaccoSeed = {
  no: string
  material: 'TSG-REG' | 'TSG-MLD'
  sak: number
  note: string
  createdAt: string
  steps: TobStep[]
  /** Terbit setelah Approve + Tugaskan Putaway. */
  sticker?: { at: string; batch: string; qr: string; rack: string; task: string; parts: [string, number][] }
  /** Putaway selesai: rak aktual & sisa stok di unit (Sak). */
  placed?: { rack: string; remainingSak: number }
}

const TOBACCO: TobaccoSeed[] = [
  {
    no: 'TBM-260814-001',
    material: 'TSG-REG',
    sak: 200,
    note: 'Hasil rajang minggu lalu',
    createdAt: '2026-08-14 07:30',
    steps: [
      ['ACT_TOB_REC_SEND', 'ACT_TOB_ADMIN_RM', 'yulia', '2026-08-14 07:30'],
      ['ACT_TOB_REC_APP', 'ACT_TOB_SPV_RM', 'galih', '2026-08-14 07:50'],
      ['ACT_TOB_REC_STORE', 'ACT_TOB_OP_RM', 'karno', '2026-08-14 08:05']
    ],
    sticker: {
      at: '2026-08-14 07:55',
      batch: 'TSG-REG-260814-01',
      qr: 'TSG-REG:20260814:01:0001',
      rack: 'R01-B04-L01-P01',
      task: 'PWR-260814-002',
      parts: [['karno', 200]]
    },
    placed: { rack: 'R01-B04-L01-P01', remainingSak: 7 }
  },
  {
    no: 'TBM-260816-002',
    material: 'TSG-REG',
    sak: 90,
    note: 'Hasil rajang shift 1',
    createdAt: '2026-08-16 07:40',
    steps: [
      ['ACT_TOB_REC_SEND', 'ACT_TOB_ADMIN_RM', 'yulia', '2026-08-16 07:40'],
      ['ACT_TOB_REC_APP', 'ACT_TOB_SPV_RM', 'galih', '2026-08-16 07:55'],
      ['ACT_TOB_REC_STORE', 'ACT_TOB_OP_RM', 'tono', '2026-08-16 08:15']
    ],
    sticker: {
      at: '2026-08-16 08:00',
      batch: 'TSG-REG-260816-01',
      qr: 'TSG-REG:20260816:01:0001',
      rack: 'R01-B03-L02-P01',
      task: 'PWR-260816-003',
      parts: [['tono', 90]]
    },
    placed: { rack: 'R01-B03-L02-P01', remainingSak: 90 }
  },
  {
    no: 'TBM-260818-003',
    material: 'TSG-MLD',
    sak: 40,
    note: 'Hasil rajang shift 2',
    createdAt: '2026-08-18 06:55',
    steps: [
      ['ACT_TOB_REC_SEND', 'ACT_TOB_ADMIN_RM', 'yulia', '2026-08-18 06:55'],
      ['ACT_TOB_REC_APP', 'ACT_TOB_SPV_RM', 'galih', '2026-08-18 07:05'],
      ['ACT_TOB_REC_STORE', 'ACT_TOB_OP_RM', 'karno', '2026-08-18 07:20']
    ],
    sticker: {
      at: '2026-08-18 07:10',
      batch: 'TSG-MLD-260818-01',
      qr: 'TSG-MLD:20260818:01:0001',
      rack: 'R01-B03-L03-P01',
      task: 'PWR-260818-004',
      parts: [['karno', 40]]
    },
    placed: { rack: 'R01-B03-L03-P01', remainingSak: 40 }
  },
  {
    no: 'TBM-260818-004',
    material: 'TSG-MLD',
    sak: 60,
    note: 'Hasil rajang shift 3 — belum ditaruh ke rak',
    createdAt: '2026-08-18 14:10',
    steps: [
      ['ACT_TOB_REC_SEND', 'ACT_TOB_ADMIN_RM', 'yulia', '2026-08-18 14:10'],
      ['ACT_TOB_REC_APP', 'ACT_TOB_SPV_RM', 'galih', '2026-08-18 14:20']
    ],
    sticker: {
      at: '2026-08-18 14:25',
      batch: 'TSG-MLD-260818-02',
      qr: 'TSG-MLD:20260818:02:0001',
      rack: 'R01-B02-L03-P01',
      task: 'PWR-260818-005',
      parts: [
        ['karno', 35],
        ['tono', 25]
      ]
    }
  },
  {
    no: 'TBM-260818-005',
    material: 'TSG-REG',
    sak: 120,
    note: 'Hasil rajang sore',
    createdAt: '2026-08-18 15:05',
    steps: [['ACT_TOB_REC_SEND', 'ACT_TOB_ADMIN_RM', 'yulia', '2026-08-18 15:05']]
  }
]

const TOB_ACTION_TO: Record<string, TobStatusCode> = {
  ACT_TOB_REC_SEND: 'TOB_REC_PENDING_SPV',
  ACT_TOB_REC_APP: 'TOB_REC_APPROVED',
  ACT_TOB_REC_REJ: 'TOB_REC_REJECTED',
  ACT_TOB_REC_STORE: 'TOB_REC_PUTAWAY'
}

const TOB_ACTION_FROM: Record<string, TobStatusCode> = {
  ACT_TOB_REC_SEND: 'TOB_REC_DRAFT',
  ACT_TOB_REC_APP: 'TOB_REC_PENDING_SPV',
  ACT_TOB_REC_REJ: 'TOB_REC_PENDING_SPV',
  ACT_TOB_REC_STORE: 'TOB_REC_APPROVED'
}

/* ------------------------------------------------------------------ build */

export function buildWmsInboundSeed(): SeedTables {
  const admin = USER_IDS.yulia
  const supervisor = USER_IDS.galih

  const warehouse_zone: LocalRow[] = [
    {
      ...audit(admin, '2026-08-01 07:00'),
      id: ZONE_ID,
      code: 'RM-STORAGE',
      name: 'Area Rak RM',
      warehouse_id: warehouseRmId,
      zone_type: 'STORAGE'
    },
    {
      ...audit(admin, '2026-08-01 07:00'),
      id: NG_ZONE_ID,
      code: 'QN-NG',
      name: 'Karantina NG',
      warehouse_id: warehouseRmId,
      zone_type: 'QUARANTINE_NG'
    },
    {
      ...audit(admin, '2026-08-01 07:00'),
      id: QC_AREA_ZONE_ID,
      code: 'ZQ-RM',
      name: 'Area QC RM',
      warehouse_id: warehouseRmId,
      zone_type: 'QC_STAGING'
    }
  ]
  const mt_reject_type: LocalRow[] = RM_QC_REJECT_TYPES.map(([code, name], index) => ({
    ...audit(admin, '2026-08-01 07:00'),
    id: seedId(SEED_GROUP.rejectType, index + 1),
    code: `RMQC-${code}`,
    name,
    description: null,
    categories: ['RM_QC'],
    sort: index + 1,
    is_active: true
  }))
  const mt_rack: LocalRow[] = RACKS.map(([code, name, category], index) => ({
    ...audit(admin, '2026-08-01 07:00'),
    id: seedId(SEED_GROUP.rack, index + 1),
    code,
    name,
    zone_id: ZONE_ID,
    category_restriction_id: category ? categoryId(category) : null,
    is_active: true
  }))
  const mt_rack_bin: LocalRow[] = BIN_CODES.map((code, index) => ({
    ...audit(admin, '2026-08-01 07:00'),
    id: seedId(SEED_GROUP.rackBin, index + 1),
    rack_id: seedId(SEED_GROUP.rack, Number(code.slice(1, 3))),
    code,
    level: code.split('-')[2],
    column: code.split('-')[1],
    is_active: true
  }))

  const rm_arrival: LocalRow[] = []
  const rm_arrival_line: LocalRow[] = []
  ARRIVALS.forEach((arrival, arrivalIndex) => {
    const arrivalId = seedId(SEED_GROUP.arrival, arrivalIndex + 1)
    rm_arrival.push({
      ...audit(admin, arrival.at),
      id: arrivalId,
      arrival_no: arrival.no,
      sj_no: arrival.sj,
      supplier_id: supplierId(arrival.supplier),
      received_at: utc(arrival.at)
    })
    arrival.lines.forEach(([material, po, qty, batch, palletized]) => {
      rm_arrival_line.push({
        ...audit(admin, arrival.at),
        id: lineIdByBatch.get(batch) as string,
        arrival_id: arrivalId,
        po_no: po,
        material_id: materialId(material),
        qty,
        unit_id: purchaseUnitOf(material),
        batch_no: batch,
        line_status: palletized ? 'PALLETIZED' : 'WAITING_PALLETIZING',
        is_over_delivery: false,
        over_delivery_qty: 0
      })
    })
  })

  const pallet: LocalRow[] = PALLETS.map(([code, cycle, , inQc], index) => ({
    ...audit(admin, '2026-08-05 07:00'),
    id: seedId(SEED_GROUP.pallet, index + 1),
    code,
    pallet_kind: 'RM_QUARANTINE',
    zone_id: null,
    capacity: null,
    recommended: null,
    unit_type: null,
    description: 'QR Pallet Karantina (reusable)',
    pallet_status: inQc ? 'IN_QC' : 'EMPTY',
    cycle_count: cycle,
    is_active: true
  }))

  let contentNo = 0
  const rm_pallet_content: LocalRow[] = PALLETS.flatMap(([code, , contents]) =>
    contents.map(([batch, qty]) => {
      contentNo += 1
      return {
        ...audit(admin, '2026-08-17 15:00'),
        id: seedId(SEED_GROUP.palletContent, contentNo),
        pallet_id: palletId(code),
        arrival_line_id: lineIdByBatch.get(batch),
        qty
      }
    })
  )

  const quarantineIdOf = (no: string) =>
    seedId(SEED_GROUP.quarantineEntry, QUARANTINE.findIndex(([qrt]) => qrt === no) + 1)
  const qtyOfBatch = (batch: string) =>
    ARRIVALS.flatMap(arrival => arrival.lines).find(line => line[3] === batch)?.[2] ?? 0

  const QC_RESULT_MINUTES = 45
  const qcDoneAtOf = (qrt: string) => {
    const done = QC_ASSIGNMENTS.filter(([, entry, , , , , , status]) => entry === qrt && status !== 'QC_ASSIGNED')
    const last = done[done.length - 1]
    if (!last) return null
    const [hour, minute] = last[5].split(':').map(Number)
    const total = hour * 60 + minute + QC_RESULT_MINUTES
    return `${last[4]} ${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
  }

  const rm_quarantine_entry: LocalRow[] = QUARANTINE.map(([no, batch, palletCode, at, good, ng, done], index) => ({
    ...audit(admin, at),
    id: seedId(SEED_GROUP.quarantineEntry, index + 1),
    quarantine_no: no,
    pallet_id: palletCode ? palletId(palletCode) : null,
    arrival_line_id: lineIdByBatch.get(batch),
    qty: qtyOfBatch(batch),
    quarantine_status: done ? 'QC_COMPLETED' : 'IN_QUARANTINE',
    qty_good: good,
    qty_ng: ng,
    is_qc_completed: done,
    entered_at: utc(at),
    /** QC tuntas = assignment terakhir baris ini selesai (prototype `rm.quarantine.qc_date`). */
    qc_completed_at: done ? utc(qcDoneAtOf(no) ?? at) : null,
    warning_level: null
  }))

  const rm_tobacco_receipt: LocalRow[] = []
  const rm_tobacco_receipt_line: LocalRow[] = []
  const wxl_submission: LocalRow[] = []
  const wxl_submission_log: LocalRow[] = []
  const rm_unit: LocalRow[] = []
  const rm_putaway_task: LocalRow[] = []
  const rm_putaway_task_item: LocalRow[] = []
  const ng_ticket: LocalRow[] = []

  /* Hasil QC OPP-125 (Good + NG) dan tugas penempatannya PWR-260818-001. */
  const opp = lineIdByBatch.get('OPP-125-260817-01')
  const oppTaskId = seedId(SEED_GROUP.putawayTask, 1)
  QC_RESULT_UNITS.forEach(([uid, qr, qty, flag, reco], index) => {
    const unitId = seedId(SEED_GROUP.rmUnit, 200 + index + 1)
    rm_unit.push({
      ...audit(USER_IDS.karno, '2026-08-18 08:45'),
      id: unitId,
      uid,
      qr_code: qr,
      material_id: materialId('OPP-125'),
      batch_no: 'OPP-125-260817-01',
      supplier_id: supplierId('SUP-BMJ'),
      po_no: 'PO-2608-110',
      qty_small: qty * smallPerBigOf('OPP-125'),
      rack_bin_id: null,
      warehouse_zone_id: null,
      flag,
      unit_status: 'AWAITING_PUTAWAY',
      qc_date: utc('2026-08-18 08:45'),
      arrival_line_id: opp,
      quarantine_entry_id: quarantineIdOf('QRT-260817-005'),
      unit_big_snapshot: 'Roll',
      small_per_big_snapshot: smallPerBigOf('OPP-125'),
      is_leftover_wip: false,
      stored_at: null
    })
    if (flag === 'NG')
      ng_ticket.push({
        ...audit(USER_IDS.karno, '2026-08-18 08:45'),
        id: seedId(SEED_GROUP.ngTicket, 1),
        ticket_no: 'NGT-260818-001',
        unit_id: unitId,
        supplier_id: supplierId('SUP-BMJ'),
        qty,
        parameter_result: 'Cut to Cut',
        placement_rack_bin_id: null,
        placement_warehouse_zone_id: null,
        ticket_status: 'OPEN',
        quarantine_entry_id: quarantineIdOf('QRT-260817-005'),
        qc_assignment_id: seedId(SEED_GROUP.qcAssignment, 1)
      })
    rm_putaway_task_item.push({
      ...audit(USER_IDS.karno, '2026-08-18 08:45'),
      id: seedId(SEED_GROUP.putawayTaskItem, index + 1),
      task_id: oppTaskId,
      unit_id: unitId,
      recommended_rack_bin_id: reco ? rackBinId(reco) : null,
      recommended_warehouse_zone_id: reco ? ZONE_ID : NG_ZONE_ID,
      actual_rack_bin_id: null,
      actual_warehouse_zone_id: null,
      qty_assigned: qty,
      qty_placed: 0,
      qty_short: null,
      picked_at: null,
      override_reason: null
    })
  })
  rm_putaway_task.push({
    ...audit(USER_IDS.karno, '2026-08-18 08:45'),
    id: oppTaskId,
    task_no: 'PWR-260818-001',
    operator_id: USER_IDS.karno,
    task_status: 'ASSIGNED',
    phase: 'PICK',
    note: null
  })

  const rejectIdOf = (name: string | null) => {
    const index = RM_QC_REJECT_TYPES.findIndex(([, label]) => label === name)
    return index >= 0 ? seedId(SEED_GROUP.rejectType, index + 1) : null
  }
  const addMinutes = (date: string, hhmm: string, minutes: number) => {
    const [hour, minute] = hhmm.split(':').map(Number)
    const total = hour * 60 + minute + minutes
    return `${date} ${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
  }
  /** Pelaku tiap aksi WF-QC-01: jadwal = Supervisor, scan/simpan/putaway = operator, setujui/kembalikan = Admin WH RM. */
  const ACTION_ACTOR: Record<QcActionName, 'ACT_QC_SPV_RM' | 'ACT_QC_OP_RM' | 'ACT_QC_ADMIN_RM'> = {
    ACT_QC_SCHEDULE: 'ACT_QC_SPV_RM',
    ACT_QC_START: 'ACT_QC_OP_RM',
    ACT_QC_RECORD: 'ACT_QC_OP_RM',
    ACT_QC_APPROVE_PRINT: 'ACT_QC_ADMIN_RM',
    ACT_QC_RETURN: 'ACT_QC_ADMIN_RM',
    ACT_QC_STORE_ALL: 'ACT_QC_OP_RM'
  }
  /** Menit setelah jam mulai QC untuk tiap aksi (jadwal dibuat 07:00 hari itu). */
  const ACTION_OFFSET: Record<QcActionName, number> = {
    ACT_QC_SCHEDULE: 0,
    ACT_QC_START: 0,
    ACT_QC_RECORD: 40,
    ACT_QC_RETURN: 70,
    ACT_QC_APPROVE_PRINT: 45,
    ACT_QC_STORE_ALL: 120
  }

  const rm_qc_assignment: LocalRow[] = QC_ASSIGNMENTS.map(
    ([no, qrt, operator, qty, date, start, duration, status], index) => {
      const id = seedId(SEED_GROUP.qcAssignment, index + 1)
      const submissionId = seedId(SEED_GROUP.wxlSubmission, 300 + index + 1)
      const path = QC_PATH_OVERRIDE[no] ?? QC_ACTION_PATH[status]
      const stepAt = (action: QcActionName) =>
        action === 'ACT_QC_SCHEDULE' ? `${date} 07:00` : addMinutes(date, start, ACTION_OFFSET[action])
      wxl_submission.push({
        ...audit(supervisor, stepAt('ACT_QC_SCHEDULE')),
        id: submissionId,
        workflow_id: WF_QC_01_ID,
        entity: 'rm_qc_assignment',
        entity_id: id,
        source: 'ACTION',
        current_status_id: qcStatusId(status),
        last_action_id: qcActionId(path[path.length - 1]),
        workflow_snapshot: null
      })
      path.forEach((action, step) => {
        const [from, to] = QC_ACTION_STATUS[action]
        const actor = ACTION_ACTOR[action]
        const by = actor === 'ACT_QC_OP_RM' ? USER_IDS[operator] : actor === 'ACT_QC_ADMIN_RM' ? admin : supervisor
        wxl_submission_log.push({
          ...audit(by, stepAt(action)),
          id: seedId(SEED_GROUP.wxlSubmissionLog, 3000 + (index + 1) * 10 + step),
          workflow_id: WF_QC_01_ID,
          submission_id: submissionId,
          action_id: qcActionId(action),
          actor_id: qcActorId(actor),
          from_status_id: qcStatusId(from),
          to_status_id: qcStatusId(to),
          approval_total_count: 1,
          approval_approved_count: 1,
          approval_rejected_count: 0,
          reason: action === 'ACT_QC_RETURN' ? (QC_RETURN_REASON[no] ?? null) : null,
          entity: 'rm_qc_assignment',
          entity_id: id
        })
      })
      const result = QC_RESULT_OF[no]
      const codes = QC_RESERVED_CODES[no] ?? [null, null]
      return {
        ...audit(supervisor, stepAt('ACT_QC_SCHEDULE')),
        id,
        assignment_no: no,
        quarantine_entry_id: quarantineIdOf(qrt),
        operator_id: USER_IDS[operator],
        qty,
        scheduled_date: date,
        start_time: `${start}:00`,
        duration_min: duration,
        qc_status: QC_STATUS_COLUMN[status],
        actual_start: status === 'QC_ASSIGNED' ? null : utc(`${date} ${start}`),
        done_at: status === 'QC_DONE' || status === 'QC_IN_STORAGE' ? utc(stepAt('ACT_QC_APPROVE_PRINT')) : null,
        /** [ERD-GAP] unit Good / NG yang lahir dari assignment ini (kolom QR UNIT prototype) + hasil per assignment (ERD-16). */
        unit_id: no === 'QSC-260818-001' ? seedId(SEED_GROUP.rmUnit, 201) : null,
        ng_unit_id: no === 'QSC-260818-001' ? seedId(SEED_GROUP.rmUnit, 202) : null,
        qty_good: result?.[0] ?? null,
        qty_ng: result?.[1] ?? null,
        ng_reason: result?.[2] ?? null,
        /** ERD-ADD (laporan 27): alasan Not Good terpilih — nilai awal saat draf dikembalikan. */
        ng_reason_ids: result?.[2] ? [rejectIdOf(result[2])].filter(Boolean) : [],
        good_unit_code: codes[0],
        ng_unit_code: codes[1],
        status_id: qcStatusId(status),
        submission_id: submissionId
      }
    }
  )

  TOBACCO.forEach((receipt, index) => {
    const receiptId = seedId(SEED_GROUP.tobaccoReceipt, index + 1)
    const submissionId = seedId(SEED_GROUP.wxlSubmission, 200 + index + 1)
    const last = receipt.steps[receipt.steps.length - 1]
    const finalStatus = TOB_ACTION_TO[last[0]]
    const perSak = smallPerBigOf(receipt.material)

    rm_tobacco_receipt.push({
      ...audit(admin, receipt.createdAt),
      user_updated: USER_IDS[last[2]],
      date_updated: utc(last[3]),
      id: receiptId,
      receipt_no: receipt.no,
      supplier_id: null,
      status_id: tobStatusId(finalStatus),
      submission_id: submissionId,
      /** [ERD-GAP] kolom Keterangan belum ada di ERD `rm_tobacco_receipt`. */
      remark: receipt.note,
      ...(finalStatus === 'TOB_REC_PUTAWAY' ? { is_stored: true, stored_at: utc(last[3]) } : {})
    })

    const lineId = seedId(SEED_GROUP.tobaccoReceiptLine, index + 1)
    rm_tobacco_receipt_line.push({
      ...audit(admin, receipt.createdAt),
      id: lineId,
      receipt_id: receiptId,
      material_id: materialId(receipt.material),
      qty_sak: receipt.sak,
      batch_no: receipt.sticker?.batch ?? null,
      sticker_qr: receipt.sticker?.qr ?? null
    })

    wxl_submission.push({
      ...audit(admin, receipt.steps[0][3]),
      id: submissionId,
      workflow_id: WF_TOB_01_ID,
      entity: 'rm_tobacco_receipt',
      entity_id: receiptId,
      source: 'ACTION',
      current_status_id: tobStatusId(finalStatus),
      last_action_id: tobActionId(last[0]),
      workflow_snapshot: null
    })

    receipt.steps.forEach(([action, actor, by, at, reason], stepIndex) => {
      wxl_submission_log.push({
        ...audit(USER_IDS[by], at),
        id: seedId(SEED_GROUP.wxlSubmissionLog, 2000 + (index + 1) * 10 + stepIndex),
        workflow_id: WF_TOB_01_ID,
        submission_id: submissionId,
        action_id: tobActionId(action),
        actor_id: tobActorId(actor),
        from_status_id: tobStatusId(TOB_ACTION_FROM[action]),
        to_status_id: tobStatusId(TOB_ACTION_TO[action]),
        approval_total_count: 1,
        approval_approved_count: action === 'ACT_TOB_REC_REJ' ? 0 : 1,
        approval_rejected_count: action === 'ACT_TOB_REC_REJ' ? 1 : 0,
        reason: reason ?? null,
        entity: 'rm_tobacco_receipt',
        entity_id: receiptId
      })
    })

    if (!receipt.sticker) return
    const sticker = receipt.sticker
    /*
     * Penerimaan yang sudah berdiri di rak memakai label tumpukan lama (satu label = satu tumpukan di satu posisi —
     * tidak pernah terpecah). Penerimaan yang masih menunggu putaway memakai label per Sak (keputusan user 2026-10-21
     * #1): satu `rm_unit` per Sak, kode unit ke-0001 … ke-N, satu item tugas per label, bagian petugas berurutan.
     */
    const labelCount = receipt.placed ? 1 : receipt.sak
    const prefix = sticker.qr.slice(0, sticker.qr.lastIndexOf(':') + 1)
    const labelIds = Array.from({ length: labelCount }, (_, k) => {
      const no = k === 0 ? 100 + index + 1 : 2100 + k + 1
      const unitRowId = seedId(SEED_GROUP.rmUnit, no)
      rm_unit.push({
        ...audit(admin, sticker.at),
        id: unitRowId,
        uid: `UNIT-${String(no).padStart(6, '0')}`,
        qr_code: receipt.placed ? sticker.qr : `${prefix}${String(k + 1).padStart(4, '0')}`,
        material_id: materialId(receipt.material),
        batch_no: sticker.batch,
        supplier_id: null,
        po_no: null,
        qty_small: receipt.placed ? receipt.placed.remainingSak * perSak : perSak,
        rack_bin_id: receipt.placed ? rackBinId(receipt.placed.rack) : null,
        warehouse_zone_id: receipt.placed ? ZONE_ID : null,
        flag: 'REGULAR',
        unit_status: receipt.placed ? 'IN_STOCK' : 'AWAITING_PUTAWAY',
        unit_big_snapshot: 'Sak',
        small_per_big_snapshot: perSak,
        is_leftover_wip: false,
        /** [ERD-GAP ERD-17] waktu pertama ditaruh — kunci FIFO (prototype v7.25). */
        stored_at: receipt.placed ? utc(last[3]) : null,
        /** ERD-ADD (laporan 32): label tembakau → baris penerimaannya. */
        tobacco_receipt_line_id: lineId
      })
      return unitRowId
    })

    let cursor = 0
    sticker.parts.forEach(([operator, qty], partIndex) => {
      const taskId = seedId(SEED_GROUP.putawayTask, (index + 1) * 10 + partIndex)
      rm_putaway_task.push({
        ...audit(admin, sticker.at),
        id: taskId,
        task_no: sticker.task,
        operator_id: USER_IDS[operator],
        task_status: receipt.placed ? 'COMPLETED' : 'ASSIGNED',
        phase: receipt.placed ? 'PLACE' : 'PICK',
        note: null
      })
      const mine = receipt.placed ? [labelIds[0]] : labelIds.slice(cursor, cursor + qty)
      mine.forEach((unitId, k) =>
        rm_putaway_task_item.push({
          ...audit(admin, sticker.at),
          id: receipt.placed
            ? seedId(SEED_GROUP.putawayTaskItem, (index + 1) * 10 + partIndex)
            : seedId(SEED_GROUP.putawayTaskItem, 6000 + cursor + k + 1),
          task_id: taskId,
          unit_id: unitId,
          recommended_rack_bin_id: rackBinId(sticker.rack),
          recommended_warehouse_zone_id: ZONE_ID,
          actual_rack_bin_id: receipt.placed ? rackBinId(receipt.placed.rack) : null,
          actual_warehouse_zone_id: receipt.placed ? ZONE_ID : null,
          /** Label tumpukan lama: jumlah bagian petugas; label per Sak: 1. */
          qty_assigned: receipt.placed ? qty : 1,
          qty_placed: receipt.placed ? qty : 0,
          qty_short: null,
          picked_at: receipt.placed ? utc(sticker.at) : null,
          override_reason: null
        })
      )
      cursor += qty
    })
  })

  return {
    warehouse_zone,
    mt_rack,
    mt_rack_bin,
    rm_arrival,
    rm_arrival_line,
    pallet,
    rm_pallet_content,
    rm_quarantine_entry,
    rm_qc_assignment,
    rm_tobacco_receipt,
    rm_tobacco_receipt_line,
    wxl_submission,
    wxl_submission_log,
    rm_unit,
    rm_putaway_task,
    rm_putaway_task_item,
    ng_ticket,
    mt_reject_type
  }
}
